// deviceFingerprint.js - Device fingerprinting service for fraud prevention

/**
 * Device Fingerprinting Service
 * Collects browser and device information to generate a unique fingerprint
 * Compatible with older browsers and includes graceful error handling
 */

class DeviceFingerprintService {
  constructor() {
    this.fingerprintData = {};
    this.isReady = false;
  }

  /**
   * Main method to collect all fingerprint data
   * @returns {Promise<Object>} Complete fingerprint data
   */
  async collectFingerprint() {
    try {
      // Collect all fingerprint components
      const data = {
        // Basic browser info
        userAgent: this.getUserAgent(),
        language: this.getLanguage(),
        platform: this.getPlatform(),

        // Screen and display
        screen: this.getScreenInfo(),
        viewport: this.getViewport(),
        colorDepth: this.getColorDepth(),
        pixelRatio: this.getPixelRatio(),

        // Browser capabilities
        touchSupport: this.getTouchSupport(),
        webgl: await this.getWebGLInfo(),
        canvas: this.getCanvasFingerprint(),
        fonts: await this.getAvailableFonts(),

        // System info
        timezone: this.getTimezone(),
        cookiesEnabled: this.getCookiesEnabled(),
        localStorageEnabled: this.getLocalStorageEnabled(),
        sessionStorageEnabled: this.getSessionStorageEnabled(),

        // Network and performance
        connection: this.getConnectionInfo(),
        hardwareConcurrency: this.getHardwareConcurrency(),
        deviceMemory: this.getDeviceMemory(),

        // Audio context fingerprint
        audioContext: await this.getAudioContextFingerprint(),

        // Additional entropy
        plugins: this.getPlugins(),
        mimeTypes: this.getMimeTypes(),
        batteryInfo: await this.getBatteryInfo(),

        // Timestamp for freshness
        timestamp: Date.now(),
      };

      this.fingerprintData = data;
      this.isReady = true;

      return data;
    } catch (error) {
      console.warn('Error collecting fingerprint:', error);
      return this.getBasicFingerprint();
    }
  }

  /**
   * Fallback basic fingerprint for older browsers
   */
  getBasicFingerprint() {
    return {
      userAgent: navigator.userAgent || 'unknown',
      language: navigator.language || 'unknown',
      platform: navigator.platform || 'unknown',
      screen: `${screen.width}x${screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown',
      timestamp: Date.now(),
    };
  }

  /**
   * Get user agent string
   */
  getUserAgent() {
    try {
      return navigator.userAgent || 'unknown';
    } catch {
      return 'unknown';
    }
  }

  /**
   * Get browser language preferences
   */
  getLanguage() {
    try {
      return {
        primary: navigator.language,
        all: navigator.languages || [navigator.language],
      };
    } catch {
      return { primary: 'unknown', all: ['unknown'] };
    }
  }

  /**
   * Get platform information
   */
  getPlatform() {
    try {
      return navigator.platform || 'unknown';
    } catch {
      return 'unknown';
    }
  }

  /**
   * Get screen information
   */
  getScreenInfo() {
    try {
      return {
        width: screen.width,
        height: screen.height,
        availWidth: screen.availWidth,
        availHeight: screen.availHeight,
        orientation: screen.orientation ? screen.orientation.type : 'unknown',
      };
    } catch {
      return {
        width: 0,
        height: 0,
        availWidth: 0,
        availHeight: 0,
        orientation: 'unknown',
      };
    }
  }

  /**
   * Get viewport dimensions
   */
  getViewport() {
    try {
      return {
        width: window.innerWidth,
        height: window.innerHeight,
      };
    } catch {
      return { width: 0, height: 0 };
    }
  }

  /**
   * Get color depth
   */
  getColorDepth() {
    try {
      return screen.colorDepth || 0;
    } catch {
      return 0;
    }
  }

  /**
   * Get pixel ratio
   */
  getPixelRatio() {
    try {
      return window.devicePixelRatio || 1;
    } catch {
      return 1;
    }
  }

  /**
   * Detect touch support
   */
  getTouchSupport() {
    try {
      return {
        maxTouchPoints: navigator.maxTouchPoints || 0,
        touchEvent: 'ontouchstart' in window,
        touchStart: 'ontouchstart' in window,
      };
    } catch {
      return { maxTouchPoints: 0, touchEvent: false, touchStart: false };
    }
  }

  /**
   * Get WebGL renderer information
   */
  async getWebGLInfo() {
    try {
      const canvas = document.createElement('canvas');
      const gl =
        canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

      if (!gl) return { supported: false };

      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');

      return {
        supported: true,
        vendor: debugInfo
          ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)
          : gl.getParameter(gl.VENDOR),
        renderer: debugInfo
          ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
          : gl.getParameter(gl.RENDERER),
        version: gl.getParameter(gl.VERSION),
        shadingLanguageVersion: gl.getParameter(gl.SHADING_LANGUAGE_VERSION),
      };
    } catch {
      return { supported: false };
    }
  }

  /**
   * Generate canvas fingerprint
   */
  getCanvasFingerprint() {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) return 'unsupported';

      // Draw some text and shapes for fingerprinting
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Device fingerprint canvas test', 2, 2);
      ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
      ctx.fillRect(100, 5, 80, 20);

      return canvas.toDataURL();
    } catch {
      return 'error';
    }
  }

  /**
   * Detect available fonts
   */
  async getAvailableFonts() {
    try {
      // Common fonts to test
      const baseFonts = ['monospace', 'sans-serif', 'serif'];
      const testFonts = [
        'Arial',
        'Arial Black',
        'Comic Sans MS',
        'Courier New',
        'Georgia',
        'Helvetica',
        'Impact',
        'Lucida Console',
        'Tahoma',
        'Times New Roman',
        'Trebuchet MS',
        'Verdana',
        'Roboto',
        'Open Sans',
        'Montserrat',
      ];

      const availableFonts = [];
      const testText = 'mmmmmmmmmmlli';
      const testSize = '72px';

      // Create test elements
      const container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      container.style.top = '-9999px';
      container.style.visibility = 'hidden';
      document.body.appendChild(container);

      // Test each font
      for (const font of testFonts) {
        let isAvailable = false;

        for (const baseFont of baseFonts) {
          const span1 = document.createElement('span');
          const span2 = document.createElement('span');

          span1.style.fontFamily = baseFont;
          span1.style.fontSize = testSize;
          span1.textContent = testText;

          span2.style.fontFamily = `${font}, ${baseFont}`;
          span2.style.fontSize = testSize;
          span2.textContent = testText;

          container.appendChild(span1);
          container.appendChild(span2);

          if (
            span1.offsetWidth !== span2.offsetWidth ||
            span1.offsetHeight !== span2.offsetHeight
          ) {
            isAvailable = true;
          }

          container.removeChild(span1);
          container.removeChild(span2);

          if (isAvailable) break;
        }

        if (isAvailable) {
          availableFonts.push(font);
        }
      }

      document.body.removeChild(container);
      return availableFonts;
    } catch {
      return [];
    }
  }

  /**
   * Get timezone information
   */
  getTimezone() {
    try {
      return {
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        offset: new Date().getTimezoneOffset(),
      };
    } catch {
      return { timezone: 'unknown', offset: 0 };
    }
  }

  /**
   * Check if cookies are enabled
   */
  getCookiesEnabled() {
    try {
      return navigator.cookieEnabled || false;
    } catch {
      return false;
    }
  }

  /**
   * Check localStorage availability
   */
  getLocalStorageEnabled() {
    try {
      const test = 'test';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Check sessionStorage availability
   */
  getSessionStorageEnabled() {
    try {
      const test = 'test';
      sessionStorage.setItem(test, test);
      sessionStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get connection information
   */
  getConnectionInfo() {
    try {
      const connection =
        navigator.connection ||
        navigator.mozConnection ||
        navigator.webkitConnection;
      if (!connection) return { supported: false };

      return {
        supported: true,
        effectiveType: connection.effectiveType,
        type: connection.type,
        downlink: connection.downlink,
        rtt: connection.rtt,
      };
    } catch {
      return { supported: false };
    }
  }

  /**
   * Get hardware concurrency (CPU cores)
   */
  getHardwareConcurrency() {
    try {
      return navigator.hardwareConcurrency || 0;
    } catch {
      return 0;
    }
  }

  /**
   * Get device memory
   */
  getDeviceMemory() {
    try {
      return navigator.deviceMemory || 0;
    } catch {
      return 0;
    }
  }

  /**
   * Generate audio context fingerprint
   */
  async getAudioContextFingerprint() {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return 'unsupported';

      const context = new AudioContext();

      // Create audio fingerprint
      const oscillator = context.createOscillator();
      const analyser = context.createAnalyser();
      const gain = context.createGain();
      const scriptProcessor = context.createScriptProcessor(4096, 1, 1);

      gain.gain.value = 0;
      oscillator.type = 'triangle';
      oscillator.frequency.value = 10000;

      oscillator.connect(analyser);
      analyser.connect(scriptProcessor);
      scriptProcessor.connect(gain);
      gain.connect(context.destination);

      oscillator.start(0);

      // Get frequency data
      const freqData = new Float32Array(analyser.frequencyBinCount);
      analyser.getFloatFrequencyData(freqData);

      oscillator.stop();
      context.close();

      return Array.from(freqData.slice(0, 30)).join(',');
    } catch {
      return 'error';
    }
  }

  /**
   * Get browser plugins
   */
  getPlugins() {
    try {
      const plugins = [];
      for (let i = 0; i < navigator.plugins.length; i++) {
        const plugin = navigator.plugins[i];
        plugins.push({
          name: plugin.name,
          filename: plugin.filename,
          description: plugin.description,
        });
      }
      return plugins;
    } catch {
      return [];
    }
  }

  /**
   * Get MIME types
   */
  getMimeTypes() {
    try {
      const mimeTypes = [];
      for (let i = 0; i < navigator.mimeTypes.length; i++) {
        const mimeType = navigator.mimeTypes[i];
        mimeTypes.push({
          type: mimeType.type,
          description: mimeType.description,
          suffixes: mimeType.suffixes,
        });
      }
      return mimeTypes;
    } catch {
      return [];
    }
  }

  /**
   * Get battery information (if available)
   */
  async getBatteryInfo() {
    try {
      if ('getBattery' in navigator) {
        const battery = await navigator.getBattery();
        return {
          charging: battery.charging,
          level: Math.round(battery.level * 100),
          chargingTime: battery.chargingTime,
          dischargingTime: battery.dischargingTime,
        };
      }
      return { supported: false };
    } catch {
      return { supported: false };
    }
  }

  /**
   * Generate a hash from the fingerprint data
   * @param {Object} data - Fingerprint data
   * @returns {Promise<string>} SHA-256 hash
   */
  async generateHash(data = null) {
    try {
      const fingerprintData = data || this.fingerprintData;
      const jsonString = JSON.stringify(
        fingerprintData,
        Object.keys(fingerprintData).sort(),
      );

      // Use Web Crypto API if available
      if (window.crypto && window.crypto.subtle) {
        const encoder = new TextEncoder();
        const dataBuffer = encoder.encode(jsonString);
        const hashBuffer = await window.crypto.subtle.digest(
          'SHA-256',
          dataBuffer,
        );
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
      }

      // Fallback simple hash for older browsers
      return this.simpleHash(jsonString);
    } catch (error) {
      console.warn('Error generating hash:', error);
      return this.simpleHash(JSON.stringify(data || this.fingerprintData));
    }
  }

  /**
   * Simple hash function for older browsers
   */
  simpleHash(str) {
    let hash = 0;
    if (str.length === 0) return hash.toString();

    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }

    return Math.abs(hash).toString(16);
  }

  /**
   * Create stable fingerprint data by excluding volatile/easily changeable fields
   * @param {Object} fullData - Complete fingerprint data
   * @returns {Object} Stable fingerprint data
   */
  createStableFingerprint(fullData) {
    const stableData = { ...fullData };

    // Remove volatile data that changes frequently
    delete stableData.timestamp;
    delete stableData.batteryInfo; // Battery level/charging status changes
    delete stableData.viewport; // Window size can be easily changed

    // Remove connection info as it can vary by network/location
    delete stableData.connection;

    // Keep screen info but remove orientation (can change by rotating device/window)
    if (stableData.screen) {
      const { orientation, ...screenWithoutOrientation } = stableData.screen;
      stableData.screen = screenWithoutOrientation;
    }

    // Remove audio context as it can be inconsistent
    delete stableData.audioContext;

    // Keep only core browser capabilities, not specific values that might fluctuate
    if (stableData.touchSupport) {
      // Keep touch capability but not specific touch points which can vary
      stableData.touchSupport = {
        hasTouch:
          stableData.touchSupport.maxTouchPoints > 0 ||
          stableData.touchSupport.touchEvent ||
          stableData.touchSupport.touchStart,
      };
    }

    return stableData;
  }

  /**
   * Get complete fingerprint with both full and stable hashes
   */
  async getFingerprint() {
    const data = await this.collectFingerprint();
    const fullHash = await this.generateHash(data);

    // Generate stable hash from filtered data
    const stableData = this.createStableFingerprint(data);
    const stableHash = await this.generateHash(stableData);

    return {
      hash: fullHash,
      stableHash: stableHash,
      data,
      stableData,
      version: '1.0.0',
    };
  }
}

export default DeviceFingerprintService;
